import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import FileUploadActions from "./FileUploadActions";
import typeCheckingStore from "data/util/typeCheckingStore";
import hybridConfig from "lib/HybridConfig";
import DataManager from "./DataManager";

/*
 This store keeps track of queued/in-progress/errored file uploads in one big bucket.
 */

// finds the index of the provided file within the state.
export const getFileIndexInState = (state, file) => _.findIndex(state, f => f.id === file.id);

export const filterFilesByStatus = (state, statuses) =>
    state.filter(f =>
        statuses.includes(f._status),
    ) || [];

export const filterFilesByFieldAndStatus = (
    state,
    field,
    record_id,
    statuses = [],
) =>
    filterFilesByStatus(state, statuses).filter(f =>
        f._field === field &&
        f._record_id === record_id
    ) || [];

// finds the provided file in the state and returns a new instance of it.
export const mergeStateFileWithFile = (state, file) => {
    const fileIndex = getFileIndexInState(state, file);
    if(fileIndex !== -1) {
        return { ...state[fileIndex], ...file};
    }
    return {...file};
};

// merges a state a with the provided file
export const replaceStateFileWithFile = (state, file, createOnNotFound = false) => {
    const newState = [...state];
    // find file index or add it.
    const fileIndex = getFileIndexInState(newState, file);
    if(fileIndex !== -1) {
        newState[fileIndex] = file;
    }
    else if(createOnNotFound) {
        // else add it to the newState.
        newState.push(file);
    }
    return newState;
};

// removes the provided file from the state.
export const removeFileFromState = (state, file) => {
    const newState = [...state];
    const fileIndex = getFileIndexInState(newState, file);
    if(fileIndex !== -1) {
        newState.splice(fileIndex, 1);
    }
    return newState;
};

// checks the current state and determines if there is room to start uploading a queued file.
export const checkConcurrentUploads = (state, action = {}) => {
    const queuedFiles = state.filter(f => f._status === Hybrid.FileInfo.FILEUPLOADSTATES.QUEUED);
    if(queuedFiles.length === 0) {
        // nothing to upload at this time.
        return state;
    }
    const inProgressFiles = state.filter(f => f._status === Hybrid.FileInfo.FILEUPLOADSTATES.INPROGRESS);
    const concurrentUploads = hybridConfig.get("fileUpload.concurrentLimit", 4);
    if (inProgressFiles.length < concurrentUploads) {
        // there is room to start one or more file uploads
        const newState = [...state];
        for (let i = 0; i + inProgressFiles.length < concurrentUploads && i < queuedFiles.length; i++) {
            // change the file to in-progress.
            const file = {
                ...queuedFiles[i],
                _status: Hybrid.FileInfo.FILEUPLOADSTATES.INPROGRESS,
                _progress: 0
            };
            const fileIndex = getFileIndexInState(state, file);
            newState[fileIndex] = file; // replace the file

            if (action.recordKey && action.recordId && action.fieldKey) {
                // Start the file upload
                DataManager.uploadFile(
                    action.recordKey,
                    action.recordId,
                    action.fieldKey,
                    file
                );
            }
        }
        return newState;
    }
    // the upload queue is currently full.
    console.log("Maximum concurrent uploads reached. Waiting for in-progress queue to shrink.");
    return state;
};

// reducer action handlers
export const handleQueuedAction = (state, action) => {
    const file = mergeStateFileWithFile(state, action.file);
    file._status = Hybrid.FileInfo.FILEUPLOADSTATES.QUEUED;
    file._progress = 0;
    return checkConcurrentUploads(
        replaceStateFileWithFile(state, file, true), // create on notFound
        action
    );
};

export const handleProgressAction = (state, action) => {
    const file = mergeStateFileWithFile(state, action.file);
    if(action.promise) {
        file._promise = action.promise;// used for xhr cancel/abort
    }
    file._status = Hybrid.FileInfo.FILEUPLOADSTATES.INPROGRESS;
    file._progress = action.progress;
    return replaceStateFileWithFile(state, file);
};

export const handleCancelAction = (state, action) => {
    const file = mergeStateFileWithFile(state, action.file);
    file._status = Hybrid.FileInfo.FILEUPLOADSTATES.ABORTED;
    file._progress = 0;
    file._promise && file._promise.abort();
    return checkConcurrentUploads(
        removeFileFromState(state, file),
        action
    );
};

export const handleCompleteAction = (state, action) =>
    checkConcurrentUploads(
        removeFileFromState(state, action.file),
        action
    );

export const handleErrorAction = (state, action) => {
    const file = mergeStateFileWithFile(state, action.file);
    file._status = Hybrid.FileInfo.FILEUPLOADSTATES.ERROR;
    file._progress = 0;
    file._promise = undefined;
    file._error = action.error;
    return checkConcurrentUploads(
        replaceStateFileWithFile(state, file),
        action
    );
};

class FileUploadStore extends ReduceStore {
    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return [];
    }

    reduce(state, action) {
        switch (action.type) {
            case FileUploadActions.QUEUED:
                return handleQueuedAction(state, action);
            case FileUploadActions.PROGRESS:
                return handleProgressAction(state, action);
            case FileUploadActions.CANCEL:
                return handleCancelAction(state, action);
            case FileUploadActions.COMPLETE:
                return handleCompleteAction(state, action);
            case FileUploadActions.ERROR:
                return handleErrorAction(state, action);
            default:
                return state;
        }
    }

    getInProgressFilesByField(field, record_id) {
        return filterFilesByFieldAndStatus(
            this.getState(),
            field,
            record_id,
            [
                Hybrid.FileInfo.FILEUPLOADSTATES.INPROGRESS,
                Hybrid.FileInfo.FILEUPLOADSTATES.QUEUED,
                Hybrid.FileInfo.FILEUPLOADSTATES.ERROR,
            ]
        );
    }

    getAllInProgressFiles() {
        return filterFilesByStatus(
            this.getState(),
            [
                Hybrid.FileInfo.FILEUPLOADSTATES.INPROGRESS,
                Hybrid.FileInfo.FILEUPLOADSTATES.QUEUED,
                Hybrid.FileInfo.FILEUPLOADSTATES.ERROR,
            ],
        );
    }

}

FileUploadStore.stateTypes = PropTypes.arrayOf(
    PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        _index: PropTypes.number.isRequired,
        filename: PropTypes.string.isRequired,
        contentType: PropTypes.string.isRequired,
        size: PropTypes.number,
        _org: PropTypes.string.isRequired,
        _impl: PropTypes.string.isRequired,
        _version: PropTypes.string.isRequired,
        _entity: PropTypes.string.isRequired,
        _field: PropTypes.string.isRequired,
        _record_id: PropTypes.string.isRequired,
        _progress: PropTypes.number,
        _status: PropTypes.string,
        _data: PropTypes.any,
        _promise: PropTypes.object,
        _onComplete: PropTypes.func
    }),
);

export default typeCheckingStore(new FileUploadStore());



// WEBPACK FOOTER //
// ./app/data/FileUploadStore.js